import dayjs from "dayjs";

export const APP_LOCALES = [
  "en",
  "tr",
  "es",
  "de",
  "ja",
  "ro",
  "el",
  "fr",
  "it",
  "sv",
  "hu",
] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

export const LANGUAGE_LABELS: Record<AppLocale, string> = {
  en: "English",
  tr: "Turkish",
  es: "Spanish",
  de: "German",
  ja: "Japanese",
  ro: "Romanian",
  el: "Greek",
  fr: "French",
  it: "Italian",
  sv: "Swedish",
  hu: "Hungarian",
};

export const SOURCE_LOCALE = "en" as const satisfies AppLocale;

export type TargetLocale = Exclude<AppLocale, typeof SOURCE_LOCALE>;

export const TARGET_LOCALES: TargetLocale[] = APP_LOCALES.filter(
  (locale): locale is TargetLocale => locale !== SOURCE_LOCALE,
);

export const ENGLISH_UI_LOCALES = ["en-US", "en-GB"] as const;

export type EnglishUiLocale = (typeof ENGLISH_UI_LOCALES)[number];

export type UiLocale = EnglishUiLocale | TargetLocale;

export const UI_LOCALES: readonly UiLocale[] = [...ENGLISH_UI_LOCALES, ...TARGET_LOCALES];

export const DEFAULT_ENGLISH_UI_LOCALE = "en-US" as const satisfies EnglishUiLocale;

export function isUiLocale(code: string): code is UiLocale {
  return (UI_LOCALES as readonly string[]).includes(code);
}

export function isEnglishUiLocale(code: string): code is EnglishUiLocale {
  return (ENGLISH_UI_LOCALES as readonly string[]).includes(code);
}

function normalizeCode(value: string): string {
  const [languagePart = "", regionPart] = value.split("-");
  const language = languagePart.toLowerCase();
  return regionPart ? `${language}-${regionPart.toUpperCase()}` : language;
}

export function resolveUiLocale(
  detected: string | undefined,
  browserLocale?: string,
): UiLocale | undefined {
  if (!detected) {
    return undefined;
  }

  const code = normalizeCode(detected);
  const [language] = code.split("-");

  if (isUiLocale(code)) {
    return code;
  }
  if (language === SOURCE_LOCALE) {
    if (code !== SOURCE_LOCALE) {
      return undefined;
    }
    const browserCode = browserLocale ? normalizeCode(browserLocale) : undefined;
    return browserCode && isEnglishUiLocale(browserCode) ? browserCode : DEFAULT_ENGLISH_UI_LOCALE;
  }
  return isUiLocale(language) ? language : undefined;
}

export function toDayjsLocale(uiLocale: string | undefined): string {
  const code = normalizeCode(uiLocale ?? SOURCE_LOCALE).toLowerCase();
  if (code === "en-us") {
    return SOURCE_LOCALE;
  }
  return dayjs.Ls[code] ? code : SOURCE_LOCALE;
}

export const UI_LOCALE_LABEL_KEYS: Record<UiLocale, string> = {
  "en-US": "englishUS",
  "en-GB": "englishUK",
  tr: "turkish",
  es: "spanish",
  de: "german",
  ja: "japanese",
  ro: "romanian",
  el: "greek",
  fr: "french",
  it: "italian",
  sv: "swedish",
  hu: "hungarian",
};
